import { useState, useEffect } from 'react';
import { Mail, Users, Send, Building2, Shield, FileText, Network } from 'lucide-react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';
import { USER_TYPES } from '../../utils/constants';
import emailTemplateService, { type EmailTemplate } from '../../services/emailTemplate.service';
import roleService, { type Role } from '../../services/role.service';
import channelService, { type Channel } from '../../services/channel.service';

interface InviteUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (data: InviteData) => Promise<void>;
  /**
   * 'channel-admin' is SuperAdmin-only. The server permits a SuperAdmin to
   * provision any user type, but refuses type 1 and type 5 from anyone below —
   * a ChannelAdmin inviting a peer would grow the set of admins over their own
   * channel, bypassing POST /channels/:id/admins. Callers must gate on type 1.
   */
  inviteType: 'super-admin' | 'channel-admin' | 'tenant-admin';
  tenantId?: string; // Required for tenant-admin and channel-admin; ignored for super-admin
  tenantName?: string;
}

export interface InviteData {
  emails: string[];
  userType: number;
  tenantId: string;
  // Owning channel — set only for channel-admin invites. The API creates the
  // channel membership at invite time so the admin is scoped on acceptance.
  channelId?: string;
  roleId?: string;
  emailTemplateId?: string;
  expiresInDays: number;
}

const InviteUsersModal = ({
  isOpen,
  onClose,
  onInvite,
  inviteType,
  tenantId,
  tenantName,
}: InviteUsersModalProps) => {
  const toast = useToast();
  const isSuperAdminInvite = inviteType === 'super-admin';
  const isChannelAdminInvite = inviteType === 'channel-admin';
  // Roles are tenant-scoped, so only a TenantAdmin gets one. SuperAdmins and
  // ChannelAdmins have no tenant and therefore no role to assign.
  const needsRole = inviteType === 'tenant-admin';
  const userType = isSuperAdminInvite
    ? USER_TYPES.SUPER_ADMIN
    : isChannelAdminInvite
      ? USER_TYPES.CHANNEL_ADMIN
      : USER_TYPES.TENANT_ADMIN;
  const [loading, setLoading] = useState(false);
  const [emailsText, setEmailsText] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [selectedChannelId, setSelectedChannelId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [expiresInDays, setExpiresInDays] = useState(7);

  // Data for dropdowns
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // Channel picker — channel-admin invites only. Fetched fresh on open; the
  // membership is created server-side at invite time. Preselect the sole
  // channel when there is exactly one so the common case is one click.
  useEffect(() => {
    if (!isOpen || !isChannelAdminInvite) {
      setChannels([]);
      return;
    }
    let cancelled = false;
    channelService.listAll(undefined, { showLoading: false })
      .then((items) => {
        if (cancelled) return;
        setChannels(items);
        if (items.length === 1) setSelectedChannelId(items[0].id);
      })
      .catch(() => { if (!cancelled) setChannels([]); });
    return () => { cancelled = true; };
  }, [isOpen, isChannelAdminInvite]);

  // Fetch templates and (for tenant-admin) roles on open.
  // Auto-select the least-privilege Viewer role (if one exists) and the Admin
  // Invitation email template (if one exists). Falls back to first role /
  // Welcome template.
  useEffect(() => {
    if (isOpen) {
      setLoadingData(true);
      const rolePromise = !needsRole || !tenantId
        ? Promise.resolve({ items: [] as Role[] })
        : roleService.list({ tenantId }).catch(() => ({ items: [] }));
      Promise.all([
        emailTemplateService.listContentTemplates().catch(() => []),
        rolePromise,
      ])
        .then(([templatesData, rolesData]) => {
          setTemplates(templatesData);
          const roleItems = rolesData.items || [];
          setRoles(roleItems);

          // Template auto-select:
          //   SuperAdmin invite → "Super Admin Invitation"
          //   TenantAdmin invite → "Admin Invitation" (non-SuperAdmin)
          // Fall back to the first "welcome" template if the preferred one
          // isn't present (old deployments).
          const lower = (s: string) => s.toLowerCase();
          const superAdminTemplate = templatesData.find((t) =>
            lower(t.name).includes('super admin invitation'),
          );
          const adminTemplate = templatesData.find((t) => {
            const n = lower(t.name);
            return n.includes('admin invitation') && !n.includes('super');
          });
          const welcomeTemplate = templatesData.find((t) =>
            lower(t.name).includes('welcome'),
          );
          const template = isSuperAdminInvite
            ? (superAdminTemplate || adminTemplate || welcomeTemplate)
            : (adminTemplate || welcomeTemplate);
          if (template) {
            setSelectedTemplateId(template.id);
          }

          // Role: default to Viewer (least-privilege), fall back to first role
          if (!isSuperAdminInvite && roleItems.length > 0) {
            const defaultRole =
              roleItems.find((r) => r.role_type === 'Viewer') || roleItems[0];
            if (defaultRole) {
              setSelectedRoleId(defaultRole.id);
            }
          }
        })
        .finally(() => setLoadingData(false));
    }
  }, [isOpen, tenantId, isSuperAdminInvite, needsRole]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmailsText('');
      setSelectedRoleId('');
      setSelectedChannelId('');
      setExpiresInDays(7);
    }
  }, [isOpen]);

  const parseEmails = (text: string): string[] => {
    // Split by newlines, commas, or semicolons
    const emails = text
      .split(/[\n,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.length > 0);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.filter((e) => emailRegex.test(e));
  };

  const handleSubmit = async () => {
    const emails = parseEmails(emailsText);

    if (emails.length === 0) {
      toast.error('No Valid Emails', 'Please enter at least one valid email address');
      return;
    }

    // Role is mandatory for TenantAdmin invites — a user with no role has no
    // permissions at all (permission lookup returns empty), so we refuse to
    // create such a user to prevent accidental lockouts.
    if (needsRole && !selectedRoleId) {
      toast.error('Role Required', 'Please select a role. Users without a role have no access.');
      return;
    }

    // Channel is mandatory for a channel-admin invite: the whole point of this
    // flow is to land the admin scoped to a channel. (Assigning additional
    // channels later still happens from the channel's Administrators tab.)
    if (isChannelAdminInvite && !selectedChannelId) {
      toast.error('Channel Required', 'Please select the channel this administrator will manage.');
      return;
    }

    // Check for duplicates
    const uniqueEmails = [...new Set(emails)];
    if (uniqueEmails.length !== emails.length) {
      toast.warning('Duplicates Removed', `${emails.length - uniqueEmails.length} duplicate emails were removed`);
    }

    setLoading(true);
    try {
      await onInvite({
        emails: uniqueEmails,
        userType,
        // SuperAdmin and ChannelAdmin have no tenant. The provisioning endpoint
        // exempts both types from the tenantId requirement, so send an empty
        // string for either rather than a placeholder.
        tenantId: (isSuperAdminInvite || isChannelAdminInvite) ? '' : (tenantId || ''),
        channelId: isChannelAdminInvite ? selectedChannelId : undefined,
        roleId: needsRole ? (selectedRoleId || undefined) : undefined,
        emailTemplateId: selectedTemplateId || undefined,
        expiresInDays,
      });
      // Success toast is emitted by the parent's onInvite handler — it has
      // richer per-result detail (provisioned vs already_exists vs invalid).
      onClose();
    } catch (error) {
      console.error('Failed to send invitations:', error);
      toast.error('Invitation Failed', 'Failed to send invitations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const emailCount = parseEmails(emailsText).length;

  const footer = (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-500">
        {emailCount} valid email{emailCount !== 1 ? 's' : ''} entered
      </span>
      <div className="flex items-center space-x-3">
        <button onClick={onClose} className="btn-secondary" disabled={loading}>
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="btn-primary inline-flex items-center space-x-2"
          disabled={loading || emailCount === 0}
        >
          <Send className="w-4 h-4" />
          <span>{loading ? 'Sending...' : 'Send Invitations'}</span>
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Invite Users"
      size="2xl"
      footer={footer}
    >
      <div className="space-y-6">
        {/* Scope Info Banner */}
        {isSuperAdminInvite ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center space-x-3">
            <Shield className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                Inviting <strong>Super Administrators</strong>
              </p>
              <p className="text-xs text-amber-700">
                Super Admins have system-wide access across all tenants. They are not scoped to any single tenant.
              </p>
            </div>
          </div>
        ) : isChannelAdminInvite ? (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start space-x-3">
            <Network className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-indigo-900">
                Inviting <strong>Channel Administrators</strong>
              </p>
              <p className="text-xs text-indigo-700 mt-1">
                A Channel Admin manages the tenants inside the channel they administer, without
                gaining global access. Pick the channel below — they will administer it as soon as
                they accept the invitation, with no extra assignment step.
              </p>
              <p className="text-xs text-indigo-700 mt-1">
                To give an admin an <strong>additional</strong> channel later, use that channel’s{' '}
                <strong>Administrators</strong> tab.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-center space-x-3">
            <Building2 className="w-5 h-5 text-primary-600" />
            <div>
              <p className="text-sm font-medium text-primary-900">
                Inviting Tenant Administrators to: <strong>{tenantName}</strong>
              </p>
              <p className="text-xs text-primary-700">
                Users will be associated with this tenant after registration.
              </p>
            </div>
          </div>
        )}

        {/* Email Addresses */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <span>Email Addresses</span>
            <span className="text-red-500">*</span>
          </label>
          <textarea
            value={emailsText}
            onChange={(e) => setEmailsText(e.target.value)}
            placeholder="Enter email addresses (one per line, or separated by commas)&#10;&#10;user1@company.com&#10;user2@company.com&#10;user3@company.com"
            className="input-base w-full h-32 font-mono text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter one email per line, or separate with commas. Invalid emails will be ignored.
          </p>
        </div>

        {/* Channel — channel-admin invites only. Mandatory: the invite scopes
            the admin to this channel on acceptance. */}
        {isChannelAdminInvite && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
              <Network className="w-4 h-4 text-gray-400" />
              <span>
                Channel <span className="text-red-500">*</span>
              </span>
            </label>
            <select
              value={selectedChannelId}
              onChange={(e) => setSelectedChannelId(e.target.value)}
              className="input-base w-full"
              disabled={loadingData || channels.length === 0}
              required
            >
              <option value="">
                {channels.length === 0 ? 'No channels available — create one first' : 'Select a channel…'}
              </option>
              {channels.map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              The admin will manage the tenants inside this channel. You can grant more channels later.
            </p>
          </div>
        )}

        {/* Role Assignment — TenantAdmin invites only. Mandatory. */}
        {needsRole && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
              <Users className="w-4 h-4 text-gray-400" />
              <span>
                Assign Role <span className="text-red-500">*</span>
              </span>
            </label>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="input-base w-full"
              disabled={loadingData}
              required
            >
              <option value="">Select a role…</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name} {role.role_type ? `(${role.role_type})` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Defaults to Viewer (read-only). Choose a higher role to grant create/update/delete.
            </p>
          </div>
        )}

        {/* Email Template */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
            <FileText className="w-4 h-4 text-gray-400" />
            <span>Welcome Email Template</span>
          </label>
          <select
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            className="input-base w-full"
            disabled={loadingData}
          >
            <option value="">Default welcome email</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Select a template for the welcome email, or use the system default
          </p>
        </div>

        {/* Expiration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Invitation Expires In
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Math.max(1, Math.min(30, parseInt(e.target.value) || 7)))}
              min={1}
              max={30}
              className="input-base w-20"
            />
            <span className="text-sm text-gray-600">days</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Users must complete registration within this time (1-30 days)
          </p>
        </div>

        {/* Info Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>What happens next:</strong>
          </p>
          <ol className="text-sm text-blue-700 list-decimal list-inside mt-2 space-y-1">
            <li>Each user receives an invitation email with a one-time activation link</li>
            <li>They click the link to set their name and password on VSN+</li>
            <li>They verify their email and sign in</li>
          </ol>
        </div>
      </div>
    </Modal>
  );
};

export default InviteUsersModal;
